import { inject, type InjectionToken } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MsalService,
  type MsalGuardConfiguration,
} from '@azure/msal-angular';
import type { AccountInfo } from '@azure/msal-browser';
import { firstValueFrom } from 'rxjs';
import { AUTH_CONFIG } from './model/auth-config.token';

const DEFAULT_POST_LOGIN_PATH = '/';

const MSAL_GUARD_CONFIG_TOKEN =
  MSAL_GUARD_CONFIG as unknown as InjectionToken<MsalGuardConfiguration>;

/**
 * Sends authenticated users off `/login`. Account presence alone is not enough:
 * MsalGuard may land here after silent refresh fails while the expired account
 * is still cached, which would bounce `/` ↔ `/login` forever.
 */
export const azureLoginRedirectGuard: CanActivateFn = async () => {
  const msalService = inject(MsalService);
  const router = inject(Router);
  const account = resolveAccount(msalService);
  const scopes = resolveScopes();

  if (!account || scopes.length === 0) {
    return true;
  }

  try {
    await firstValueFrom(
      msalService.acquireTokenSilent({ account, scopes }),
    );
    return router.createUrlTree([DEFAULT_POST_LOGIN_PATH]);
  } catch {
    return true;
  }
};

function resolveAccount(msalService: MsalService): AccountInfo | null {
  const activeAccount = msalService.instance.getActiveAccount();

  if (activeAccount) {
    return activeAccount;
  }

  const [account] = msalService.instance.getAllAccounts();

  if (account) {
    msalService.instance.setActiveAccount(account);
    return account;
  }

  return null;
}

function resolveScopes(): string[] {
  const config = inject(AUTH_CONFIG, { optional: true });

  if (config?.protectedResourceMap) {
    for (const scopes of config.protectedResourceMap.values()) {
      if (scopes?.length) {
        return scopes;
      }
    }
  }

  const authRequest = inject(MSAL_GUARD_CONFIG_TOKEN, {
    optional: true,
  })?.authRequest;

  if (authRequest && typeof authRequest !== 'function' && authRequest.scopes) {
    return authRequest.scopes;
  }

  return [];
}
